<script setup lang="ts">
import { computed } from 'vue';
import { IconName, IconSize } from './icon.constants';

type Props = {
  /** Which glyph to render. */
  name: IconName;
  /** Rendered size. */
  size?: IconSize;
};

const props = withDefaults(defineProps<Props>(), {
  size: IconSize.MD,
});

// Pixel dimensions per size (replicating the Figma Icon Size variant 24/20/16).
const sizePx: Record<IconSize, number> = {
  [IconSize.SM]: 16,
  [IconSize.MD]: 20,
  [IconSize.LG]: 24,
};

const px = computed(() => sizePx[props.size]);

// Stroke-based path data on a 24×24 grid. Icons inherit color via
// `currentColor` and scale down cleanly at smaller sizes. Copied from the
// admin icon assets.
const iconPaths: Record<IconName, string[]> = {
  [IconName.ADD_CIRCLE]: [
    'M12 22C17.5 22 22 17.5 22 12C22 6.5 17.5 2 12 2C6.5 2 2 6.5 2 12C2 17.5 6.5 22 12 22Z',
    'M8 12H16',
    'M12 16V8',
  ],
  [IconName.CLOSE]: ['M7.5 16L16 7.5', 'M16 16L7.5 7.5'],
};

const paths = computed(() => iconPaths[props.name]);
</script>

<template>
  <svg
    :width="px"
    :height="px"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    stroke-width="1.5"
    stroke-linecap="round"
    stroke-linejoin="round"
    aria-hidden="true"
    class="inline-block shrink-0"
  >
    <path v-for="(d, i) in paths" :key="i" :d="d" />
  </svg>
</template>
